import { Grid, Stack } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { GContainer, GItem, GItemContainer, MiddleGItem } from '../components/common/grids'
import { SubTitle } from '../components/common/subTitle'
import { AttentionLabel } from '../components/common/attentionLabel'
import { ErrorMessageCard } from '../components/common/errorMessageCard'
import { BottomButtonGroup } from '../components/common/bottomButtonGroup'
import { ButtonOutlinedWarningL } from '../components/common/buttons/buttonOutlinedWarningL'
import { BackButton } from '../components/common/buttons/backButton'
import { Form } from '../components/common/inputs/form'
import { CheckBox } from '../components/common/inputs/checkBox'
import { RadioButtonGroup } from '../components/common/inputs/radioButtonGroup'
import { Select } from '../components/common/inputs/select'
import { DatePicker } from '../components/common/inputs/datePicker'
import { TextBox } from '../components/common/inputs/textBox'
import { translate } from '../../i18n'
import { getNow } from '../../utils/dateUtil'
import { yesNo } from '../../containers/common/constant/classification'
import { GetCategoryMstDto, AddQuestion } from '../../dataAccess/webApi/dto/categoryMstDto'
import { useAction } from '../../containers/memberModifyChildVaccination/memberModifyChildVaccinationService'
import { isEditable } from '../../containers/common/category'

export const MemberModifyChildVaccination = () => {
  const {
    CATEGORY_KEY,
    EXIST_FLAG_NAME,
    formMethods,
    categoryMst,
    hasUsagePermission,
    onSubmit,
    childrenResultCode,
  } = useAction()

  const targetCategory = categoryMst?.find((value) => value.category === CATEGORY_KEY)
  if (!targetCategory) return (<BackButton />)
  
  return (
    <Form formMethods={formMethods} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Grid container>
          <Grid item md={3}></Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              {targetCategory ?
                (<>
                  <SubTitle title={targetCategory.altCategoryName ?? targetCategory.name} />
                  <InputForm
                    categoryMst={targetCategory}
                    existFlagName={EXIST_FLAG_NAME}
                    hasUsagePermission={hasUsagePermission}
                  />
                </>)
                :
                (<>
                  {translate('memberModifyChildVaccination.error.noCategoryMst')}
                </>)
              }
            </Stack>
          </Grid>
        </Grid>
        {!!childrenResultCode && (
          <ErrorMessageCard
            messages={[translate(`memberModifyConfirmationChild.error.childrenResultCode${childrenResultCode}`)]}
          />
        )}
        <BottomButtonGroup>
          <ButtonOutlinedWarningL fullWidth type="submit" disabled={!isEditable(targetCategory.editCitizen, hasUsagePermission)}>
            {translate('memberModifyChildVaccination.button.decision')}
          </ButtonOutlinedWarningL>
          <BackButton />
        </BottomButtonGroup>
      </Stack>
    </Form>
  )
}

// 入力フォームプロパティ
interface InputFormProps {
  categoryMst: GetCategoryMstDto
  existFlagName?: string
  hasUsagePermission: boolean
}

// 入力フォーム
const InputForm = (props: InputFormProps) => {
  const { existFlagName, categoryMst, hasUsagePermission } = props
  const methods = useFormContext()
  const existFlag = useWatch({ name: existFlagName ?? '', control: methods.control })
  const disabled = !isEditable(categoryMst.editCitizen, hasUsagePermission)
  return (
    <>
      {existFlagName && categoryMst.checkExistence && categoryMst.checkTextNo && categoryMst.checkTextYes &&
        <GItemContainer xs={12} rowSpacing={1}>
          <MiddleGItem xs={5} sm={1}>{categoryMst.name}</MiddleGItem>
          <GItem xs={7} sm={11}>
            <RadioButtonGroup
              name={existFlagName}
              label={categoryMst.name}
              buttonValueLabels={[
                { label: categoryMst.checkTextNo, value: yesNo.no },
                { label: categoryMst.checkTextYes, value: yesNo.yes },
              ]}
              row
              disabled={disabled}
            />
          </GItem>
        </GItemContainer>
      }
      {(categoryMst.checkExistence === yesNo.no || existFlag === yesNo.yes) && categoryMst?.masterData && (
        categoryMst.masterData.map((opt, index) => {
          return (
            <GContainer key={`${categoryMst.category}.${index}`}>
              <GItem xs={1} />
              <GItem xs={11} >
                <CheckBox 
                  name={`${categoryMst.category}.${index}.flag`}
                  label={opt.name}
                  valueAtCheck={yesNo.yes}
                  disabled={disabled}
                >
                  {opt.name}
                </CheckBox>
                {opt.addQuestions && 
                  <AddQuestionUnit 
                    topName={`${categoryMst.category}.${index}`}
                    items={opt.addQuestions}
                    disabled={disabled}
                  />
                }
              </GItem>
            </GContainer>
          )
        })
      )}
    </>
  )
}

// 追加質問フォームプロパティ
interface AddQuestionUnitProps {
  topName: string
  items: AddQuestion[]
  disabled: boolean
}

// 追加質問フォーム
const AddQuestionUnit = (props: AddQuestionUnitProps) => {
  const { topName, items, disabled } = props
  
  const methods = useFormContext()
  const topFlagValue = useWatch({ name: `${topName}.flag`, control: methods.control })
  return (
    <>
      {topFlagValue === yesNo.yes && (
        items.map((item) => {
          const render = () => {
            switch(item?.fieldName){
              case "count":
                const options = item.addQuestionOptions?.map(op => ({
                  value: op.optionValue,
                  label: op.optionText,
                }));
                if (options == null) return null
              
                const listData = [{value: "", label: "未選択"}, ...options]
                return(
                  <GItemContainer xs={12} rowSpacing={1}>
                    <MiddleGItem xs={5} sm={2}>{item.questionText}</MiddleGItem>
                    <GItem xs={7} sm={10}>
                      <Select
                        label={item.questionText}
                        name={`${topName}.${item.fieldName}`}
                        valueLabels={listData}
                        disabled={disabled}
                      />
                    </GItem>
                  </GItemContainer>
                )
                
              case "vaccinationDate":
                return(
                  <GItemContainer xs={12} rowSpacing={1}>
                    <MiddleGItem xs={5} sm={2}>
                      {item.required === yesNo.yes && (
                        <AttentionLabel>*</AttentionLabel>
                      )}
                      {item.questionText}
                    </MiddleGItem>
                    <GItem xs={7} sm={10}>
                      <DatePicker
                        name={`${topName}.${item.fieldName}`}
                        label={item.questionText}
                        maxDate={getNow()}
                        required={item.required === yesNo.yes}
                        disabled={disabled}
                      />
                    </GItem>
                  </GItemContainer>
                )
                
              case "note":
                return(
                  <GItemContainer xs={12} rowSpacing={1}>
                    <GItem xs={5} sm={2}>
                      {item.required === yesNo.yes && (
                        <AttentionLabel>*</AttentionLabel>
                      )}
                      {item.maxLength != null ? `(${item.maxLength}字以内)` : ''}
                    </GItem>
                    <GItem xs={7} sm={10}>
                      <TextBox
                        label={item.questionText}
                        name={`${topName}.${item.fieldName}`}
                        maxLength={item.maxLength}
                        rows={4}
                        required={item.required === yesNo.yes}
                        multiline
                        fullWidth
                        disabled={disabled}
                      />
                    </GItem>
                  </GItemContainer>
                )
                
              default:
                return null
            }
          }
          
          return (
            <div key={`${topName}.${item.fieldName}`}>
              {render()}
            </div>
          )
          
        })
      )}
    </>
  )
}
